import {
  decodePrintAttribution
} from "../chunk-KHFIF5LY.js";
import {
  decodeMailKitCode
} from "../chunk-GKFZDSP7.js";
import {
  LocationKeySchema
} from "../chunk-L5JE4PWK.js";
import "../chunk-PZ5AY32C.js";

// src/utils/join-source.ts
var JOIN_SOURCE_VERSION = 1;
var JOIN_SOURCE_PARAM = "via";
var JOIN_TOUCH_HEADER = "X-FaveCard-Join";
var KIOSK_SESSION_HEADER = "X-FaveCard-Kiosk-Session";
var JOIN_SOURCE_CONFIG_PATH = "_config/joinSource";
function isJoinSourceSwitchOn(control) {
  return typeof control === "object" && control !== null && control.enabled === true;
}
function decodeJoinSourceAvailability(body) {
  return typeof body === "object" && body !== null && body.state === "on" ? "on" : "off";
}
var JOIN_SURFACES = ["web", "kiosk", "coupon_claim", "owner_test"];
var JOIN_CHANNELS = [
  "mail_kit",
  "print",
  "social",
  "messaging",
  "email",
  "website",
  "google",
  "search",
  "qr",
  "staff_app",
  "invite",
  "in_store",
  "partner",
  "other",
  "unknown"
];
var JOIN_CHANNEL_SOURCES = ["code", "inferred", "none"];
var PLACE_CODES = {
  ig: { channel: "social", detail: "instagram" },
  fb: { channel: "social", detail: "facebook" },
  tt: { channel: "social", detail: "tiktok" },
  tw: { channel: "social", detail: "x" },
  li: { channel: "social", detail: "linkedin" },
  yt: { channel: "social", detail: "youtube" },
  wa: { channel: "messaging", detail: "whatsapp" },
  ms: { channel: "messaging", detail: "messenger" },
  tg: { channel: "messaging", detail: "telegram" },
  sm: { channel: "messaging", detail: "sms" },
  em: { channel: "email" },
  ws: { channel: "website", detail: "button" },
  gp: { channel: "google", detail: "profile" },
  qo: { channel: "qr", detail: "onboarding" },
  qs: { channel: "qr", detail: "share_page" },
  qc: { channel: "qr", detail: "catalogue" },
  sa: { channel: "staff_app", detail: "add_customer_qr" },
  // The owner's own invitation to their regulars (activation slice, "Invite"), one code per way it went
  // out: the details are its `invite_sent.channel` values (Metrics DR-004, DR-005 §4).
  iv: { channel: "invite", detail: "share_sheet" },
  iw: { channel: "invite", detail: "whatsapp" },
  is: { channel: "invite", detail: "sms" },
  ic: { channel: "invite", detail: "copy" },
  iqw: { channel: "invite", detail: "qr_whatsapp" },
  iqs: { channel: "invite", detail: "qr_sms" }
};
var SOCIAL_GRAPHIC_SCENE_CODES = {
  wallet: "w",
  phoneCentred: "p",
  phoneAsset: "a",
  cardHero: "c",
  howItWorks: "h",
  brandedGuide: "b"
};
var SOCIAL_GRAPHIC_FORMAT_CODES = { post: "p", square: "s", story: "t" };
var JOIN_PLACE_LINKS = ["ig", "fb", "tt", "wa", "ws", "gp"];
function keyOf(map, code) {
  return Object.keys(map).find((key) => map[key] === code);
}
var SOCIAL_GRAPHIC_SCENE_DETAILS = {
  wallet: "wallet",
  phoneCentred: "phone_centred",
  phoneAsset: "phone_asset",
  cardHero: "card_hero",
  howItWorks: "how_it_works",
  brandedGuide: "branded_guide"
};
function socialGraphicChannel(code) {
  if (code.length !== 3 || code[0] !== "g") return null;
  const scene = keyOf(SOCIAL_GRAPHIC_SCENE_CODES, code[1]);
  const format = keyOf(SOCIAL_GRAPHIC_FORMAT_CODES, code[2]);
  return scene && format ? { channel: "social", detail: `graphic_${SOCIAL_GRAPHIC_SCENE_DETAILS[scene]}_${String(format)}` } : null;
}
function codeChannel(code) {
  if (Object.prototype.hasOwnProperty.call(PLACE_CODES, code)) return PLACE_CODES[code];
  return socialGraphicChannel(code);
}
function isJoinSourceCode(value) {
  return typeof value === "string" && /^[a-z]{2,3}$/.test(value) && codeChannel(value) !== null;
}
function socialGraphicCode(scene, format) {
  return `g${SOCIAL_GRAPHIC_SCENE_CODES[scene]}${SOCIAL_GRAPHIC_FORMAT_CODES[format]}`;
}
function readJoinSourceCode(search) {
  const params = new URLSearchParams(search);
  const values = params.getAll(JOIN_SOURCE_PARAM);
  if (params.get("demo") === "true" || params.has("preview")) return void 0;
  return values.length === 1 && isJoinSourceCode(values[0]) ? values[0] : void 0;
}
var BUSINESS_PAGE_RESERVED_SEGMENTS = /* @__PURE__ */ new Set([
  "api",
  "coupon",
  "coupons",
  "demo",
  "legal",
  "me",
  "my-cards",
  "test-guide",
  "w",
  "monitoring",
  "wallet-buttons",
  "about",
  "login",
  "signup",
  "admin",
  "health",
  "internal",
  "__"
]);
var PERSONAL_PARAMS = ["p", "customerId", "email", "firstName", "lastName", "phone", "t", "token"];
var PERSONAL_ROUTES = /* @__PURE__ */ new Set(["w", "my-cards", "me", "coupon", "coupons", "api", "__"]);
function shareableTarget(url, code) {
  if (!isJoinSourceCode(code)) throw new RangeError(`not a join source code: ${JSON.stringify(code)}`);
  const target = new URL(url);
  if (target.protocol !== "https:" && target.protocol !== "http:") throw new RangeError(`join source URL must be http(s), got ${target.protocol}`);
  const firstSegment = target.pathname.split("/").filter(Boolean)[0] ?? "";
  if (PERSONAL_PARAMS.some((name) => target.searchParams.has(name)) || PERSONAL_ROUTES.has(firstSegment)) {
    throw new RangeError("a personal link (pass, customer, contact, kiosk token or credential route) is never shared with a source code");
  }
  return target;
}
function withJoinSourceCode(url, code) {
  const target = shareableTarget(url, code);
  target.searchParams.set(JOIN_SOURCE_PARAM, code);
  return target.toString();
}
function withJoinSourcePath(url, code) {
  const target = shareableTarget(url, code);
  const segments = target.pathname.split("/").filter(Boolean);
  if (segments.length === 3 && segments[1] === "l" && LocationKeySchema.safeParse(segments[2]).success) {
    target.searchParams.set(JOIN_SOURCE_PARAM, code);
    return target.toString();
  }
  const base = segments.length > 1 && isJoinSourceCode(segments[segments.length - 1]) ? segments.slice(0, -1) : segments;
  if (base.length !== 1 && base.length !== 2) {
    throw new RangeError(`join source links are /{slug} or /{slug}/{shortCode}, got ${target.pathname}`);
  }
  if (BUSINESS_PAGE_RESERVED_SEGMENTS.has(base[0])) {
    throw new RangeError(`/${base[0]} is a Business Page route: use the code as ?${JOIN_SOURCE_PARAM}=`);
  }
  target.pathname = `/${[...base, code].join("/")}`;
  return target.toString();
}
var REFERRER_CHANNELS = {
  google: { channel: "google", detail: "referral" },
  bing: { channel: "search", detail: "bing" },
  duckduckgo: { channel: "search", detail: "duckduckgo" },
  yahoo: { channel: "search", detail: "yahoo" },
  ecosia: { channel: "search", detail: "ecosia" },
  instagram: { channel: "social", detail: "instagram" },
  facebook: { channel: "social", detail: "facebook" },
  tiktok: { channel: "social", detail: "tiktok" },
  x: { channel: "social", detail: "x" },
  linkedin: { channel: "social", detail: "linkedin" },
  youtube: { channel: "social", detail: "youtube" },
  threads: { channel: "social", detail: "threads" },
  snapchat: { channel: "social", detail: "snapchat" },
  pinterest: { channel: "social", detail: "pinterest" },
  reddit: { channel: "social", detail: "other" },
  messenger: { channel: "messaging", detail: "messenger" },
  whatsapp: { channel: "messaging", detail: "whatsapp" },
  telegram: { channel: "messaging", detail: "telegram" },
  mail: { channel: "email" },
  site: { channel: "website", detail: "referral" }
};
var GOOGLE_DOMAINS = [
  "google.com",
  "google.co.uk",
  "google.com.tr",
  "google.com.ua",
  "google.com.cy",
  "google.com.mt",
  "google.com.br",
  "google.com.mx",
  "google.com.ar",
  "google.cl",
  "google.ca",
  "google.co.in",
  "google.com.au",
  "google.co.jp",
  ...[
    "pl",
    "de",
    "at",
    "ch",
    "fr",
    "be",
    "nl",
    "lu",
    "it",
    "es",
    "pt",
    "ie",
    "dk",
    "se",
    "no",
    "fi",
    "is",
    "cz",
    "sk",
    "hu",
    "ro",
    "bg",
    "gr",
    "ee",
    "lv",
    "lt",
    "si",
    "hr",
    "rs",
    "ba",
    "me",
    "mk",
    "al",
    "md",
    "li"
  ].map((cc) => `google.${cc}`)
];
var PINTEREST_DOMAINS = [
  "pinterest.com",
  "pin.it",
  "pinterest.co.uk",
  "pinterest.com.au",
  "pinterest.com.mx",
  "pinterest.ca",
  "pinterest.cl",
  ...["de", "fr", "es", "it", "pt", "ie", "at", "ch", "se", "dk", "nz", "jp"].map((cc) => `pinterest.${cc}`)
];
var REFERRER_DOMAINS = [
  ["mail", [
    "mail.google.com",
    "outlook.live.com",
    "outlook.office.com",
    "outlook.office365.com",
    "mail.yahoo.com",
    "poczta.onet.pl",
    "poczta.wp.pl",
    "poczta.interia.pl",
    "poczta.o2.pl"
  ]],
  ["google", GOOGLE_DOMAINS],
  ["bing", ["bing.com"]],
  ["duckduckgo", ["duckduckgo.com"]],
  ["yahoo", ["yahoo.com", "yahoo.co.jp"]],
  ["ecosia", ["ecosia.org"]],
  ["instagram", ["instagram.com"]],
  ["messenger", ["messenger.com"]],
  ["facebook", ["facebook.com", "fb.com", "fb.me"]],
  ["tiktok", ["tiktok.com"]],
  ["x", ["x.com", "twitter.com", "t.co"]],
  ["linkedin", ["linkedin.com", "lnkd.in"]],
  ["youtube", ["youtube.com", "youtu.be"]],
  ["threads", ["threads.net", "threads.com"]],
  ["snapchat", ["snapchat.com"]],
  ["pinterest", PINTEREST_DOMAINS],
  ["reddit", ["reddit.com"]],
  ["whatsapp", ["whatsapp.com", "wa.me"]],
  ["telegram", ["telegram.org", "t.me"]]
];
var onDomain = (host, domain) => host === domain || host.endsWith(`.${domain}`);
var ANDROID_APP_REFERRERS = {
  "com.google.android.googlequicksearchbox": "google",
  "com.google.android.gm": "mail",
  "com.microsoft.office.outlook": "mail",
  "com.instagram.android": "instagram",
  "com.facebook.katana": "facebook",
  "com.facebook.orca": "messenger",
  "com.zhiliaoapp.musically": "tiktok",
  "com.twitter.android": "x",
  "com.linkedin.android": "linkedin",
  "com.whatsapp": "whatsapp",
  "org.telegram.messenger": "telegram"
};
function classifyJoinReferrer(referrer, ownHosts = []) {
  if (!referrer) return null;
  let url;
  try {
    url = new URL(referrer);
  } catch {
    return null;
  }
  if (url.protocol === "android-app:") return ANDROID_APP_REFERRERS[url.hostname.toLowerCase()] ?? null;
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  const host = url.hostname.toLowerCase().replace(/\.$/, "");
  if (/(^|\.)favecard\.co$/.test(host) || ownHosts.map((own) => own.toLowerCase()).includes(host)) return null;
  for (const [name, domains] of REFERRER_DOMAINS) if (domains.some((domain) => onDomain(host, domain))) return name;
  return "site";
}
var UTM_CHANNELS = {
  instagram: { channel: "social", detail: "instagram" },
  facebook: { channel: "social", detail: "facebook" },
  tiktok: { channel: "social", detail: "tiktok" },
  x: { channel: "social", detail: "x" },
  linkedin: { channel: "social", detail: "linkedin" },
  youtube: { channel: "social", detail: "youtube" },
  whatsapp: { channel: "messaging", detail: "whatsapp" },
  messenger: { channel: "messaging", detail: "messenger" },
  telegram: { channel: "messaging", detail: "telegram" },
  sms: { channel: "messaging", detail: "sms" },
  google: { channel: "google", detail: "referral" },
  email: { channel: "email" },
  other: { channel: "other" }
};
var UTM_SOURCES = [
  [/^(ig|instagram)$/, "instagram"],
  [/^(fb|facebook)$/, "facebook"],
  [/^tiktok$/, "tiktok"],
  [/^(x|twitter|t\.co)$/, "x"],
  [/^linkedin$/, "linkedin"],
  [/^youtube$/, "youtube"],
  [/^(wa|whatsapp)$/, "whatsapp"],
  [/^messenger$/, "messenger"],
  [/^telegram$/, "telegram"],
  [/^sms$/, "sms"],
  [/^(google|gbp|google_business_profile|gmb)$/, "google"],
  [/^(email|e-mail|newsletter|mail)$/, "email"]
];
function classifyJoinUtm(search) {
  const params = new URLSearchParams(search);
  const source = params.get("utm_source")?.trim().toLowerCase() ?? "";
  const medium = params.get("utm_medium")?.trim().toLowerCase() ?? "";
  if (!source && !medium) return null;
  for (const [pattern, name] of UTM_SOURCES) if (pattern.test(source)) return name;
  if (/^(email|e-mail|newsletter)$/.test(medium)) return "email";
  if (medium === "sms") return "sms";
  return "other";
}
var has = (map, key) => Object.prototype.hasOwnProperty.call(map, key);
function encodeJoinTouch(touch) {
  const code = touch.code !== void 0 && isJoinSourceCode(touch.code) ? touch.code : void 0;
  const referrer = touch.referrer !== void 0 && has(REFERRER_CHANNELS, touch.referrer) ? touch.referrer : void 0;
  const utm = touch.utm !== void 0 && has(UTM_CHANNELS, touch.utm) ? touch.utm : void 0;
  if (code === void 0 && referrer === void 0 && utm === void 0) return null;
  return `1.${code ?? "-"}.${referrer ?? "-"}.${utm ?? "-"}`;
}
function decodeJoinTouch(value) {
  if (typeof value !== "string" || value.length > 40) return null;
  const match = /^1\.([a-z]{2,3}|-)\.([a-z]{1,12}|-)\.([a-z]{1,12}|-)$/.exec(value);
  if (!match) return null;
  const [, code, referrer, utm] = match;
  if (code !== "-" && !isJoinSourceCode(code)) return null;
  if (referrer !== "-" && !has(REFERRER_CHANNELS, referrer)) return null;
  if (utm !== "-" && !has(UTM_CHANNELS, utm)) return null;
  if (code === "-" && referrer === "-" && utm === "-") return null;
  return {
    ...code !== "-" && { code },
    ...referrer !== "-" && { referrer },
    ...utm !== "-" && { utm }
  };
}
var IN_APP_BROWSERS = [
  [/FBAN\/Messenger|MessengerForiOS|MessengerLite|Orca-Android/i, { channel: "messaging", detail: "messenger" }],
  [/Barcelona/, { channel: "social", detail: "threads" }],
  [/Instagram/i, { channel: "social", detail: "instagram" }],
  [/FBAN|FBAV|FB_IAB|FBIOS|FB4A/, { channel: "social", detail: "facebook" }],
  [/musical_ly|BytedanceWebview|TikTok|trill_/i, { channel: "social", detail: "tiktok" }],
  [/Snapchat/i, { channel: "social", detail: "snapchat" }],
  [/Pinterest/i, { channel: "social", detail: "pinterest" }],
  [/LinkedInApp/i, { channel: "social", detail: "linkedin" }],
  [/Twitter/i, { channel: "social", detail: "x" }],
  [/\bLine\//, { channel: "messaging", detail: "line" }],
  [/WhatsApp/i, { channel: "messaging", detail: "whatsapp" }],
  [/Telegram/i, { channel: "messaging", detail: "telegram" }],
  [/\bGSA\//, { channel: "google", detail: "referral" }]
];
function inferJoinChannelFromUserAgent(userAgent) {
  if (!userAgent) return null;
  for (const [pattern, channel] of IN_APP_BROWSERS) if (pattern.test(userAgent)) return channel;
  return null;
}
var COUPON_PLACEMENTS = {
  point_of_sale: { channel: "in_store", detail: "point_of_sale" },
  table_service: { channel: "in_store", detail: "table_service" },
  print: { channel: "print" },
  email: { channel: "email" },
  whatsapp: { channel: "messaging", detail: "whatsapp" },
  instagram: { channel: "social", detail: "instagram" },
  facebook: { channel: "social", detail: "facebook" },
  social: { channel: "social", detail: "other" },
  partner: { channel: "partner" },
  other: { channel: "other" }
};
var sourced = (surface, channel, channelSource) => ({
  version: JOIN_SOURCE_VERSION,
  surface,
  channel: channel.channel,
  ...channel.detail !== void 0 && { detail: channel.detail },
  channelSource
});
function resolveJoinSource(input) {
  if (input.surface === "coupon_claim") {
    const placement = input.placementKind !== null && has(COUPON_PLACEMENTS, input.placementKind) ? COUPON_PLACEMENTS[input.placementKind] : null;
    return placement === null ? sourced("coupon_claim", { channel: "unknown" }, "none") : sourced("coupon_claim", placement, "code");
  }
  if (input.surface !== "web") return { version: JOIN_SOURCE_VERSION, surface: input.surface, channelSource: "none" };
  if (typeof input.kitCode === "string" && decodeMailKitCode(input.kitCode)) {
    return sourced("web", { channel: "mail_kit", detail: input.kitCode }, "code");
  }
  if (decodePrintAttribution(input.printMaterial)) return sourced("web", { channel: "print" }, "code");
  const touch = decodeJoinTouch(input.touch);
  if (touch?.code !== void 0) return sourced("web", codeChannel(touch.code), "code");
  if (touch?.utm !== void 0) return sourced("web", UTM_CHANNELS[touch.utm], "code");
  const app = inferJoinChannelFromUserAgent(input.userAgent);
  if (app !== null) return sourced("web", app, "inferred");
  if (touch?.referrer !== void 0) return sourced("web", REFERRER_CHANNELS[touch.referrer], "inferred");
  return sourced("web", { channel: "unknown" }, "none");
}
function catalogOf(channels) {
  const catalog = /* @__PURE__ */ new Map();
  for (const { channel, detail } of channels) {
    const details = catalog.get(channel) ?? /* @__PURE__ */ new Set();
    details.add(detail ?? null);
    catalog.set(channel, details);
  }
  return catalog;
}
var SOCIAL_GRAPHIC_CHANNELS = Object.keys(SOCIAL_GRAPHIC_SCENE_CODES).flatMap((scene) => Object.keys(SOCIAL_GRAPHIC_FORMAT_CODES).map((format) => socialGraphicChannel(socialGraphicCode(scene, format))));
var WEB_CODE_CATALOG = catalogOf([
  ...Object.values(PLACE_CODES),
  ...SOCIAL_GRAPHIC_CHANNELS,
  ...Object.values(UTM_CHANNELS),
  { channel: "print" }
]);
var WEB_INFERRED_CATALOG = catalogOf([...Object.values(REFERRER_CHANNELS), ...IN_APP_BROWSERS.map(([, channel]) => channel)]);
var COUPON_CODE_CATALOG = catalogOf(Object.values(COUPON_PLACEMENTS));
function isCatalogChannel(surface, channel, detail, channelSource) {
  if (surface === "web" && channel === "mail_kit") {
    return channelSource === "code" && detail !== void 0 && decodeMailKitCode(detail) !== null;
  }
  const catalog = surface !== "web" ? channelSource === "code" ? COUPON_CODE_CATALOG : null : channelSource === "code" ? WEB_CODE_CATALOG : WEB_INFERRED_CATALOG;
  const details = catalog?.get(channel);
  return details !== void 0 && details.has(detail ?? null);
}
function parseJoinSource(value) {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return null;
  const record = value;
  const keys = Object.keys(record);
  if (keys.some((key) => !["version", "surface", "channel", "detail", "channelSource"].includes(key))) return null;
  if (record.version !== JOIN_SOURCE_VERSION) return null;
  const { surface, channel, detail, channelSource } = record;
  if (!JOIN_SURFACES.includes(surface)) return null;
  if (detail !== void 0 && typeof detail !== "string") return null;
  if (surface === "kiosk" || surface === "owner_test") {
    if (channel !== void 0 || detail !== void 0 || channelSource !== "none") return null;
    return { version: JOIN_SOURCE_VERSION, surface, channelSource: "none" };
  }
  if (channel === "unknown") {
    if (detail !== void 0 || channelSource !== "none") return null;
  } else {
    if (channelSource !== "code" && channelSource !== "inferred") return null;
    if (!isCatalogChannel(surface, channel, detail, channelSource)) return null;
  }
  return {
    version: JOIN_SOURCE_VERSION,
    surface,
    channel,
    ...detail !== void 0 && { detail },
    channelSource
  };
}
function joinSourceProperties(source) {
  return {
    join_version: source.version,
    join_surface: source.surface,
    join_channel_source: source.channelSource,
    ...source.channel !== void 0 && { join_channel: source.channel },
    ...source.detail !== void 0 && { join_detail: source.detail }
  };
}
export {
  BUSINESS_PAGE_RESERVED_SEGMENTS,
  JOIN_CHANNELS,
  JOIN_CHANNEL_SOURCES,
  JOIN_PLACE_LINKS,
  JOIN_SOURCE_CONFIG_PATH,
  JOIN_SOURCE_PARAM,
  JOIN_SOURCE_VERSION,
  JOIN_SURFACES,
  JOIN_TOUCH_HEADER,
  KIOSK_SESSION_HEADER,
  SOCIAL_GRAPHIC_FORMAT_CODES,
  SOCIAL_GRAPHIC_SCENE_CODES,
  classifyJoinReferrer,
  classifyJoinUtm,
  decodeJoinSourceAvailability,
  decodeJoinTouch,
  encodeJoinTouch,
  inferJoinChannelFromUserAgent,
  isJoinSourceCode,
  isJoinSourceSwitchOn,
  joinSourceProperties,
  parseJoinSource,
  readJoinSourceCode,
  resolveJoinSource,
  socialGraphicCode,
  withJoinSourceCode,
  withJoinSourcePath
};
//# sourceMappingURL=join-source.js.map